import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ArrowLeft, Plus, Edit, Trash2, Loader2, FileText, Save, FileUp, Clock } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DocumentSettingsPage = () => {
  const [documentTypes, setDocumentTypes] = useState([]);
  const [settings, setSettings] = useState({
    max_file_size_mb: 10,
    allowed_extensions: ['pdf', 'doc', 'docx', 'jpg', 'png', 'xlsx'],
    retention_days: 365,
    require_document_category: true,
    auto_delete_expired: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('types');
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [typeForm, setTypeForm] = useState({
    name: '',
    description: '',
    category: 'general',
    required_for_enrollment: false,
    is_active: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [typesRes, settingsRes] = await Promise.all([
        axios.get(`${API}/admin/document-types`),
        axios.get(`${API}/admin/document-settings`)
      ]);
      setDocumentTypes(typesRes.data.types || []);
      if (settingsRes.data) {
        setSettings(prev => ({ ...prev, ...settingsRes.data }));
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/document-settings`, settings);
      toast.success('Document settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const openTypeDialog = (type = null) => {
    if (type) {
      setEditingType(type);
      setTypeForm({
        name: type.name,
        description: type.description || '',
        category: type.category || 'general',
        required_for_enrollment: type.required_for_enrollment || false,
        is_active: type.is_active !== false
      });
    } else {
      setEditingType(null);
      setTypeForm({
        name: '',
        description: '',
        category: 'general',
        required_for_enrollment: false,
        is_active: true
      });
    }
    setDialogOpen(true);
  };

  const saveType = async () => {
    if (!typeForm.name.trim()) {
      toast.error('Document type name is required');
      return;
    }
    
    setSaving(true);
    try {
      if (editingType) {
        await axios.put(`${API}/admin/document-types/${editingType.id}`, typeForm);
        toast.success('Document type updated');
      } else {
        await axios.post(`${API}/admin/document-types`, typeForm);
        toast.success('Document type created');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save document type');
    } finally {
      setSaving(false);
    }
  };

  const deleteType = async (typeId) => {
    if (!window.confirm('Are you sure you want to delete this document type?')) return;
    
    try {
      await axios.delete(`${API}/admin/document-types/${typeId}`);
      toast.success('Document type deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const toggleExtension = (ext) => {
    const extensions = settings.allowed_extensions || [];
    if (extensions.includes(ext)) {
      setSettings({ ...settings, allowed_extensions: extensions.filter(e => e !== ext) });
    } else {
      setSettings({ ...settings, allowed_extensions: [...extensions, ext] });
    }
  };

  const CATEGORIES = [
    { value: 'general', label: 'General' },
    { value: 'identification', label: 'Identification' },
    { value: 'income', label: 'Income Verification' },
    { value: 'employment', label: 'Employment' },
    { value: 'housing', label: 'Housing' },
    { value: 'medical', label: 'Medical' },
    { value: 'legal', label: 'Legal' }
  ];

  const ALL_EXTENSIONS = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'jpg', 'jpeg', 'png', 'gif', 'txt', 'csv'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="document-settings-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/settings">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Settings
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <FileText className="w-6 h-6" />
              Documents & Templates
            </h1>
            <p className="text-muted-foreground">Configure document types and upload settings</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        <Button
          variant={activeTab === 'types' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('types')}
          className="rounded-b-none"
        >
          <FileText className="w-4 h-4 mr-2" />
          Document Types
        </Button>
        <Button
          variant={activeTab === 'settings' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('settings')}
          className="rounded-b-none"
        >
          <FileUp className="w-4 h-4 mr-2" />
          Upload Settings
        </Button>
      </div>

      {/* Document Types Tab */}
      {activeTab === 'types' && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Document Types</CardTitle>
              <CardDescription>Define types of documents that can be uploaded</CardDescription>
            </div>
            <Button onClick={() => openTypeDialog()}>
              <Plus className="w-4 h-4 mr-2" />
              Add Type
            </Button>
          </CardHeader>
          <CardContent>
            {documentTypes.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type Name</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Required</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {documentTypes.map((type) => (
                    <TableRow key={type.id}>
                      <TableCell className="font-medium">{type.name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {CATEGORIES.find(c => c.value === type.category)?.label || type.category}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {type.required_for_enrollment ? (
                          <Badge className="bg-red-100 text-red-800">Required</Badge>
                        ) : (
                          <span className="text-muted-foreground">Optional</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={type.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                          {type.is_active !== false ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => openTypeDialog(type)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteType(type.id)} className="text-destructive">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No document types defined</p>
                <Button className="mt-4" onClick={() => openTypeDialog()}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create First Type
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Upload Settings Tab */}
      {activeTab === 'settings' && (
        <div className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Upload Settings</CardTitle>
                <CardDescription>Configure file upload restrictions</CardDescription>
              </div>
              <Button onClick={saveSettings} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Settings
              </Button>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Maximum File Size (MB)</Label>
                <Input
                  type="number"
                  value={settings.max_file_size_mb}
                  onChange={(e) => setSettings({ ...settings, max_file_size_mb: parseInt(e.target.value) || 10 })}
                />
              </div>

              <div>
                <Label className="mb-2 block">Allowed File Extensions</Label>
                <div className="flex flex-wrap gap-2">
                  {ALL_EXTENSIONS.map((ext) => (
                    <Badge 
                      key={ext} 
                      variant={settings.allowed_extensions?.includes(ext) ? 'default' : 'outline'}
                      className="cursor-pointer uppercase"
                      onClick={() => toggleExtension(ext)}
                    >
                      .{ext}
                    </Badge>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-2">Click to toggle allowed extensions</p>
              </div>

              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <p className="font-medium">Require Document Category</p>
                  <p className="text-sm text-muted-foreground">Users must select a category when uploading</p>
                </div>
                <Switch
                  checked={settings.require_document_category}
                  onCheckedChange={(v) => setSettings({ ...settings, require_document_category: v })}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Retention Policy
              </CardTitle>
              <CardDescription>Configure document retention and deletion</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Retention Period (days)</Label>
                <Input
                  type="number"
                  value={settings.retention_days}
                  onChange={(e) => setSettings({ ...settings, retention_days: parseInt(e.target.value) || 365 })}
                />
                <p className="text-xs text-muted-foreground">How long to keep documents after case closure</p>
              </div>

              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <p className="font-medium">Auto-Delete Expired Documents</p>
                  <p className="text-sm text-muted-foreground">Automatically remove documents past retention period</p>
                </div>
                <Switch
                  checked={settings.auto_delete_expired}
                  onCheckedChange={(v) => setSettings({ ...settings, auto_delete_expired: v })}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Document Type Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingType ? 'Edit Document Type' : 'Add Document Type'}</DialogTitle>
            <DialogDescription>Configure document type details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Type Name *</Label>
              <Input
                value={typeForm.name}
                onChange={(e) => setTypeForm({ ...typeForm, name: e.target.value })}
                placeholder="e.g., Photo ID, Pay Stub"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={typeForm.description}
                onChange={(e) => setTypeForm({ ...typeForm, description: e.target.value })}
                placeholder="Optional description"
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Select
                value={typeForm.category}
                onValueChange={(v) => setTypeForm({ ...typeForm, category: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <p className="font-medium">Required for Enrollment</p>
                <p className="text-sm text-muted-foreground">Must be uploaded during intake</p>
              </div>
              <Switch
                checked={typeForm.required_for_enrollment}
                onCheckedChange={(v) => setTypeForm({ ...typeForm, required_for_enrollment: v })}
              />
            </div>
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <p className="font-medium">Active</p>
                <p className="text-sm text-muted-foreground">Available for selection</p>
              </div>
              <Switch
                checked={typeForm.is_active}
                onCheckedChange={(v) => setTypeForm({ ...typeForm, is_active: v })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveType} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editingType ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentSettingsPage;
